import { Table } from "@tiptap/extension-table";

/**
 * Values the CSSOM hands back for a longhand that a shorthand reset. The table
 * carries `border: none` (borders live on the cells), which makes
 * `element.style.borderColor` report `currentcolor` and `element.style.borderWidth`
 * report `medium` — neither is a setting the user chose, so they must never win
 * over the `data-border-*` attributes the settings panel writes.
 */
const CSS_KEYWORDS = new Set(["currentcolor", "medium", "initial", "inherit", "unset", "revert", "none", "auto", "thin", "thick"]);

const isRealValue = (value: string | null | undefined): value is string =>
  !!value && !CSS_KEYWORDS.has(value.trim().toLowerCase());

/** `rgb(255, 0, 0)` -> `#ff0000`, so `<input type="color">` shows the right swatch. */
const toHexColor = (value: string): string => {
  const match = value.match(/rgba?\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
  if (!match) return value;
  const [r, g, b] = [match[1], match[2], match[3]].map((n) => parseInt(n, 10));
  return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
};

/** Read a numeric setting, preferring the persisted data attribute. */
const parseNumericSetting = (element: HTMLElement, dataAttr: string, inlineValue: string, fallback: number): number => {
  const fromAttr = element.getAttribute(dataAttr);
  if (fromAttr !== null) {
    const parsed = parseFloat(fromAttr);
    if (!isNaN(parsed)) return parsed;
  }
  if (isRealValue(inlineValue)) {
    const parsed = parseFloat(inlineValue);
    if (!isNaN(parsed)) return parsed;
  }
  return fallback;
};

// Helper to create responsive attribute definitions
const createResponsiveAttr = (baseName: string, defaultValue: string | null = null) => ({
  default: defaultValue,
  parseHTML: (element: HTMLElement) => element.getAttribute(`data-${baseName}`),
  renderHTML: (attributes: Record<string, unknown>) => {
    if (!attributes[`data-${baseName}`]) return {};
    return { [`data-${baseName}`]: attributes[`data-${baseName}`] as string };
  },
});

export const CustomTable = Table.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      class: {
        default: null,
        parseHTML: (element: HTMLElement) => element.getAttribute("class"),
        renderHTML: (attributes: Record<string, unknown>) => {
          if (!attributes.class) return {};
          return { class: attributes.class as string };
        },
      },
      style: {
        default: null,
        parseHTML: (element: HTMLElement) => element.getAttribute("style"),
        renderHTML: (attributes: Record<string, unknown>) => {
          if (!attributes.style) return {};
          return { style: attributes.style as string };
        },
      },
      "data-table-id": {
        default: null,
        parseHTML: (element: HTMLElement) => element.getAttribute("data-table-id"),
        renderHTML: (attributes: Record<string, unknown>) => {
          if (!attributes["data-table-id"]) return {};
          return { "data-table-id": attributes["data-table-id"] as string };
        },
      },
      "data-all-row-settings": {
        default: null,
        parseHTML: (element: HTMLElement) => element.getAttribute("data-all-row-settings"),
        renderHTML: (attributes: Record<string, unknown>) => {
          if (!attributes["data-all-row-settings"]) return {};
          return { "data-all-row-settings": attributes["data-all-row-settings"] as string };
        },
      },
      "data-cell-styles": {
        default: null,
        parseHTML: (element: HTMLElement) => element.getAttribute("data-cell-styles"),
        renderHTML: (attributes: Record<string, unknown>) => {
          if (!attributes["data-cell-styles"]) return {};
          return { "data-cell-styles": attributes["data-cell-styles"] as string };
        },
      },
      // Individual table attributes (like cell styles)
      // The persisted `data-border-*` attributes are the source of truth. Reading
      // the inline style first used to overwrite them with `currentcolor`/`medium`
      // on every parse, so saved border settings were lost on reload.
      borderColor: {
        default: "#000000",
        parseHTML: (element: HTMLElement) => {
          const fromAttr = element.getAttribute("data-border-color");
          if (isRealValue(fromAttr)) return toHexColor(fromAttr);
          if (isRealValue(element.style.borderColor)) return toHexColor(element.style.borderColor);
          return "#000000";
        },
        renderHTML: (attributes: Record<string, unknown>) => {
          return {
            "data-border-color": attributes.borderColor as string,
          };
        },
      },
      borderWidth: {
        default: 1,
        parseHTML: (element: HTMLElement) =>
          parseNumericSetting(element, "data-border-width", element.style.borderWidth, 1),
        renderHTML: (attributes: Record<string, unknown>) => {
          // Guard against NaN values
          const width = typeof attributes.borderWidth === 'number' && !isNaN(attributes.borderWidth)
            ? attributes.borderWidth
            : 1;
          return {
            "data-border-width": String(width),
          };
        },
      },
      borderRadius: {
        default: 0,
        parseHTML: (element: HTMLElement) =>
          parseNumericSetting(element, "data-border-radius", element.style.borderRadius, 0),
        renderHTML: (attributes: Record<string, unknown>) => {
          // Guard against NaN values
          const radius = typeof attributes.borderRadius === 'number' && !isNaN(attributes.borderRadius) 
            ? attributes.borderRadius 
            : 0;
          return {
            "data-border-radius": String(radius),
          };
        },
      },
      // Per-breakpoint width settings (type: auto|percent|pixel, value, alignment, horizontalScroll, rowGap)
      // Stored as compact string: "type:value:alignment:scroll:gap" per breakpoint
      "data-width-xs": createResponsiveAttr("width-xs"),
      "data-width-sm": createResponsiveAttr("width-sm"),
      "data-width-md": createResponsiveAttr("width-md"),
      "data-width-lg": createResponsiveAttr("width-lg"),
      "data-width-xl": createResponsiveAttr("width-xl"),
      // Store border-spacing as a data attribute so it survives TipTap re-renders
      "data-border-spacing": {
        default: null,
        parseHTML: (element: HTMLElement) => {
          // Try to extract from style first, then from data attribute
          const styleSpacing = element.style.borderSpacing;
          if (styleSpacing) return styleSpacing;
          return element.getAttribute("data-border-spacing");
        },
        renderHTML: (attributes: Record<string, unknown>) => {
          const spacing = attributes["data-border-spacing"] as string | null;
          if (!spacing) return {};
          return { 
            "data-border-spacing": spacing,
            style: `border-collapse: separate; border-spacing: ${spacing};`
          };
        },
      },
    };
  },
});


